import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';

type ThisApi = ApiDefinition['/task-efficiency-reports/{creatorId}']['GET'];
type Params = ThisApi['params'] & ThisApi['query'];
type Output = ThisApi['response'];

/**
 * SWR-based hook for fetching the paginated list of task efficiency reports.
 * Returns cached data across remounts to prevent unnecessary reloads.
 */
export const useTaskEfficiencyReports = (
  params: Params | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output, ApiError> => {
  const key = useMemo((): SwrKey => {
    if (!params?.creatorId) return null;
    const path = `/task-efficiency-reports/${params.creatorId}`;
    return [path, { params: { page: params.page, perPage: params.perPage } }];
  }, [params?.creatorId, params?.page, params?.perPage]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

/**
 * Fetches a paginated list of task efficiency reports for a creator.
 * @deprecated Please use `useTaskEfficiencyReports` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const listTaskEfficiencyReports = async ({ creatorId, page, perPage }: Params): Promise<Output> => {
  const response = await noodleApiClient.get(`/task-efficiency-reports/${creatorId}`, {
    params: { page, perPage },
  });
  return response.data;
};

export default listTaskEfficiencyReports;
